import React, { useEffect, useState } from 'react';
import { SongFormData } from '../types/songEdit';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  data: SongFormData;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ isOpen, onClose, onConfirm, data }) => {
  const [isSaving, setIsSaving] = useState(false);

  // Reset the in-flight flag whenever the modal opens fresh.
  useEffect(() => {
    if (isOpen) setIsSaving(false);
  }, [isOpen]);

  const handleConfirmClick = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onConfirm();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const renderValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : 'empty array';
    }
    if (typeof value === 'object') {
      if ('full_name' in value) {
        let result = value.full_name;
        if (value.era) result += ` (${value.era})`;
        if (value.origin) result += ` - ${value.origin}`;
        return result;
      }
      return JSON.stringify(value);
    }
    return value.toString();
  };

  const fields = [
    { label: 'Title', value: data.title },
    { 
      label: 'Composer', 
      value: data.composerData || null
    },
    { 
      label: 'Lyricist', 
      value: data.lyricistData || null
    },
    { label: 'Primary Tune', value: data.primaryTune },
    { label: 'Tune ID', value: data.tune_id },
    { label: 'Alternate Tunes', value: data.alternateTunes },
    { label: 'Tags', value: data.tags },
    { label: 'Video Link', value: data.video_link },
    { label: 'Copyright', value: data.copyright }
  ];

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Confirm Data to Save</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose} 
              aria-label="Close"
            ></button>
          </div>
          
          <div className="modal-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map(({ label, value }) => (
                    <tr key={label}>
                      <td className="fw-bold">{label}</td>
                      <td>
                        <span className={value === null ? 'text-muted' : ''}>
                          {renderValue(value)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleConfirmClick}
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : 'Confirm & Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;